import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { IAuthService } from "../services/AuthService";
import { JWTToken } from "../models/tokens";
import { HttpConfig } from "../helpers/http.config";
import { APIResponse } from "../models/APIResponse";

export abstract class BaseAPI {
  protected baseUrl: string;
  private readonly axiosInstance: AxiosInstance;
  public authService: IAuthService;

  constructor(authService: IAuthService, cfg: HttpConfig) {
    this.authService = authService;
    this.baseUrl = cfg.baseUrl
    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      headers: {
        "x-api-key": cfg.apiKey,
        Authorization: `Bearer ${cfg.accessToken}`,
        organisation: cfg.organization,
      },
    });
    this.enableInterceptors();
  }

  private enableInterceptors(): void {
    this.axiosInstance.interceptors.response.use(
      this.getSuccessResponseHandler(),
      this.getErrorResponseHandler(),
    );
  }

  private getSuccessResponseHandler() {
    return (response: any) => response;
  }

  private didAccessTokenExpire(response: any): boolean {
    return response?.data.message === "Token signature expired.";
  }

  private async regenerateAccessTokenFromRefreshToken(): Promise<JWTToken> {
    const response = await axios({
      method: "POST",
      url: `${this.baseUrl}/users/token/refresh`,
      data: {
        refreshToken: this.authService.getToken("refresh-token"),
      },
    });
    return response.data.accessToken;
  }

  private getErrorResponseHandler() {
    console.log('error response')
    return async (error: any) => {
      if (this.didAccessTokenExpire(error.response)) {
        const refreshToken = this.authService.getToken("refresh-token");
        if (refreshToken) {
          try {
            const accessToken =
              await this.regenerateAccessTokenFromRefreshToken();
            this.authService.setToken("access-token", accessToken);

            // retry request with fresh token
            error.config.headers = {
              ...error.config.headers,
              Authorization: `Bearer ${accessToken}`,
            };
            return this.axiosInstance.request(error.config);
          } catch (err) {
            this.authService.removeToken("access-token");
            this.authService.removeToken("refresh-token");
            console.error(err);
          }
        }
      }
      throw error;
    };
  }

  private withHeaders(headers?: Record<string, string>) {
    return {
      ...headers,
    };
  }

  protected get<T>(
    url: string,
    params?: AxiosRequestConfig,
    headers?: Record<string, string>,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance({
      method: "GET",
      url,
      params: params ?? undefined,
      headers: this.withHeaders(headers),
    });
  }

  protected post(
    url: string,
    data?: any,
    params?: AxiosRequestConfig,
    headers?: Record<string, string>,
  ): Promise<APIResponse<any>> {
    console.log('firing from the post', {
      url,
      data,
      params,
      headers,
    })
    return this.axiosInstance({
      method: "POST",
      url,
      data: data ?? undefined,
      params: params ?? undefined,
      headers: this.withHeaders(headers),
    });
  }
}
